import React from "react"
import { View } from "react-native"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import BFText, { H5, Small } from "@/components/standard/BFText"
import { Button } from "@/components/reusables/ui/button"
import {
  type MilestoneStatus,
  MilestoneStatusMarker,
} from "./MilestoneStatusMarker"
import { ProcessorCard } from "./ProcessorCard"
import type { MilestoneType } from "@/generated/graphql"
import { Separator } from "@/components/reusables/ui/separator"
import { MilestoneStatusBadge } from "@/screens/loans/components/dashboard/milestones/MilestoneStatusBadge"

interface MilestoneItemProps {
  milestone: MilestoneType
  isLast?: boolean
  onPress?: () => void
  onActionPress?: (action: string) => void
  specialActions: string[]
  onProcessorContact?: (
    type: "phone" | "message" | "schedule" | "email"
  ) => void
}

const formatMilestoneDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export const MilestoneItem: React.FC<MilestoneItemProps> = ({
  milestone,
  isLast = false,
  onActionPress,
  onProcessorContact,
  specialActions,
}) => {
  const isDisabled = milestone.status === "blocked"

  const isProcessorMilestone = milestone.id === "meet_your_processor"

  //
  // const specialActions = getSpecialActions()

  const renderSpecialContent = () => {
    // Special UI for "Meet your processor" milestone
    if (isProcessorMilestone) {
      return (
        <ProcessorCard
          processor={{
            name: "Sarah Johnson",
            title: "Senior Loan Processor",
            phone: "(555) 123-4567",
            email: "sarah.johnson@backflip.com",
          }}
          onPhonePress={() => onProcessorContact?.("phone")}
          onMessagePress={() => onProcessorContact?.("message")}
          onSchedulePress={() => onProcessorContact?.("schedule")}
          onEmailPress={() => onProcessorContact?.("email")}
        />
      )
    }

    return null
  }

  const renderActions = () => {
    if (specialActions.length === 0 || isDisabled) {
      return null
    }

    if (milestone.status === "blocked") {
      return null
    }

    return (
      <Horizontal className="gap-2 mt-3">
        {specialActions.map((action) => (
          <Button
            key={action}
            variant="outline"
            size="sm"
            onPress={() => onActionPress?.(action)}
          >
            <BFText>{action}</BFText>
          </Button>
        ))}
      </Horizontal>
    )
  }

  return (
    <>
      <View
        className={`relative ${isDisabled ? "opacity-50" : ""}`}
        style={{ minHeight: 60 }}
      >
        <Horizontal className="gap-4 items-start">
          {/* Status marker */}
          <View className="mt-1">
            <MilestoneStatusMarker
              status={milestone.status as MilestoneStatus}
              size={24}
            />
          </View>

          {/* Content */}
          <Vertical className="flex-1 pb-6">
            {/* Title and Status */}
            <Horizontal className="items-start justify-between mb-2">
              <View className="flex-1 mt-1">
                <H5 color={isDisabled ? "disabled" : "default"}>
                  {milestone.name}
                </H5>
              </View>

              <MilestoneStatusBadge milestone={milestone} />
            </Horizontal>

            {milestone.description && milestone.status !== "completed" && (
              <Small
                color={isDisabled ? "disabled" : "default"}
                className="mb-2"
              >
                {milestone.description}
              </Small>
            )}

            {milestone.completedDate && (
              <Small color="hint">
                Completed {formatMilestoneDate(milestone.completedDate)}
              </Small>
            )}

            {/* Actions */}
            {renderActions()}

            {/* Special milestone content */}
            {renderSpecialContent()}
          </Vertical>
        </Horizontal>
      </View>
      {!isLast && (
        <View className={"p-3 pt-0"}>
          <Separator />
        </View>
      )}
    </>
  )
}
